import React, { useEffect, useState, useLayoutEffect } from 'react';
import { Route, Routes, useLocation } from 'react-router-dom';
import App from './App';
import { Login } from './pages/Login';
import { Error } from './pages/Error';
import { NotFound } from './pages/NotFound';
import { Access } from './pages/Access';
import { useAppDispatch, useAppSelector } from './redux/hooks';
import { setColorScheme, setTheme, setComponentTheme } from './redux/actions/actionsTheme';
import { setUserField } from './redux/actions/actionsUser';

const EASTER_EGG_PINK_KEY = 'easterEggPink';
const EASTER_EGG_GREEN_KEY = 'easterEggGreen';

const AppWrapper = () => {
    const dispatch = useAppDispatch();
    const userData = useAppSelector(state => state.UsersReducer.userData);
    const themeState = useAppSelector(state => state.ThemeReducer);
    const [easterEggPinkTick, setEasterEggPinkTick] = useState(0);
    const [easterEggGreenTick, setEasterEggGreenTick] = useState(0);

    useEffect(() => {
        const onEasterEggChange = () => setEasterEggPinkTick(t => t + 1);
        const onEasterEggGreenChange = () => setEasterEggGreenTick(t => t + 1);
        window.addEventListener('easterEggPinkChange', onEasterEggChange);
        window.addEventListener('easterEggGreenChange', onEasterEggGreenChange);
        return () => {
            window.removeEventListener('easterEggPinkChange', onEasterEggChange);
            window.removeEventListener('easterEggGreenChange', onEasterEggGreenChange);
        };
    }, []);

    const isEasterEggPink = typeof window !== 'undefined' && localStorage.getItem(EASTER_EGG_PINK_KEY) === 'true';
    const isEasterEggGreen = typeof window !== 'undefined' && localStorage.getItem(EASTER_EGG_GREEN_KEY) === 'true';

    const getUserTheme = () => {
        if (userData?.theme === 'light' || userData?.theme === 'dark') {
            return userData.theme;
        }
        return 'dark';
    };
    useLayoutEffect(() => {
        const userTheme = getUserTheme();
        if (userTheme && !themeState.colorScheme) {
            dispatch(setColorScheme(userTheme));
        }
    }, []);
    
    useEffect(() => {
        const userTheme = getUserTheme();
        if (userData?.userId && userTheme && (userTheme !== themeState.colorScheme)) {
            dispatch(setColorScheme(userTheme));
        }
    }, [userData?.userId, userData?.theme]);

    const colorScheme = themeState.colorScheme || getUserTheme() || 'dark';
    const theme = themeState.theme || 'blue';
    const componentTheme = themeState.componentTheme || 'blue';
    // Пасхальные темы: pink -> magenta + light; green -> primereact green
    const effectiveColorScheme = isEasterEggPink ? 'light' : colorScheme;
    const effectiveTheme = isEasterEggPink
        ? 'magenta'
        : isEasterEggGreen
            ? 'green'
            : theme;
    const effectiveComponentTheme = isEasterEggPink
        ? 'magenta'
        : isEasterEggGreen
            ? 'green'
            : componentTheme;

    const setStyleSheetHref = (id: string, href: string) => {
        const link = document.getElementById(id) as HTMLLinkElement | null;
        if (!link) return;

        // Если остались "clone" от прошлых переключений — удаляем, чтобы не было гонок.
        const clone = document.getElementById(id + '-clone');
        if (clone) clone.remove();

        const current = link.getAttribute('href') || '';
        if (current === href) return;
        link.setAttribute('href', href);
    };

    // Единая точка применения темы. Без клонов/ожидания load — так не ломается при частых переключениях.
    useLayoutEffect(() => {
        if (!effectiveColorScheme || !effectiveTheme || !effectiveComponentTheme) return;

        setStyleSheetHref('layout-css', `assets/layout/css/${effectiveTheme}/layout-${effectiveColorScheme}.css`);
        setStyleSheetHref('theme-css', `assets/theme/${effectiveComponentTheme}/theme-${effectiveColorScheme}.css`);
    }, [effectiveColorScheme, effectiveTheme, effectiveComponentTheme, easterEggPinkTick, easterEggGreenTick]);

    let location = useLocation();

    useEffect(() => {
        window.scrollTo(0, 0);
    }, [location]);

    // <!--            const theme = localStorage.getItem('callcenter:user');-->
    // <!--            let themeJson = JSON.parse(theme);-->
    // <!--            const themeColor = themeJson?.theme;-->
    // <!--            let color = 'dark';-->
    // <!--            console.log(color)-->
    // <!--            if(themeColor) {-->
    // <!--                console.log(themeColor)-->
    // <!--                color = themeColor-->
    // <!--            }-->
    // <!--            document.getElementById("theme-css").setAttribute("href", `%PUBLIC_URL%/assets/theme/blue/theme-${color}.css`);-->
    // <!--            document.getElementById("layout-css").setAttribute("href", `%PUBLIC_URL%/assets/layout/css/blue/layout-${color}.css`);-->
    //
    //
    // <!--            console.log(themeJson)-->

    const onColorSchemeChange = (scheme: string) => {
        dispatch(setUserField({section: 'user', fieldName: 'theme', value: scheme}));
    };

    const onMenuThemeChange = (newTheme: string) => {
        dispatch(setTheme(newTheme));
    };

    const onComponentThemeChange = (newComponentTheme: string) => {
        dispatch(setComponentTheme(newComponentTheme));
    };

    return (
        <Routes>
            <Route path="/login" element={<Login colorScheme={effectiveColorScheme} />} />
            <Route path="/error" element={<Error colorScheme={effectiveColorScheme} />} />
            <Route path="/notfound" element={<NotFound colorScheme={effectiveColorScheme} />} />
            <Route path="/access" element={<Access colorScheme={effectiveColorScheme} />} />
            <Route
                path="*"
                element={
                    <App
                        colorScheme={effectiveColorScheme}
                        onColorSchemeChange={onColorSchemeChange}
                        componentTheme={effectiveComponentTheme}
                        onComponentThemeChange={onComponentThemeChange}
                        theme={effectiveTheme}
                        onMenuThemeChange={onMenuThemeChange}
                    />
                }
            />
        </Routes>
    );
};

export default AppWrapper;
